import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { chatFunctions } from '../firebase';

export interface ChatAttachment {
  url: string;
  mimeType: string;
  storagePath: string;
  name?: string;
  size?: number;
}

export interface AssistantResponse {
  conversationId: string;
  response: string;
  toolCalls?: any[];
  [key: string]: any;
}

export interface SendMessageOptions {
  vehicleId?: string;
  tankId?: string;
  context?: any;
  useMultimodal?: boolean;
  attachments?: ChatAttachment[];
}

export interface UseAIChatResult {
  sendMessage: (
    prompt: string,
    companyId: string,
    conversationId?: string | null,
    options?: SendMessageOptions
  ) => Promise<any>;
  confirmAction: (confirmPayload: any) => Promise<any>;
  listConversations: () => Promise<any>;
  getConversationMessages: (conversationId: string) => Promise<any>;
  deleteConversation: (conversationId: string) => Promise<any>;
  loading: boolean;
  error: string | null;
}

const useAIChat = (): UseAIChatResult => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = async (
    prompt: string,
    companyId: string,
    conversationId: string | null = null,
    { vehicleId, tankId, context, useMultimodal, attachments }: SendMessageOptions = {}
  ): Promise<any> => {
    setLoading(true);
    setError(null);
    try {
      const assistantRouter = httpsCallable<any, any>(chatFunctions, 'assistantRouter');
      const result = await assistantRouter({
        prompt,
        companyId,
        conversationId,
        vehicleId,
        tankId,
        context,
        useMultimodal,
        attachments,
      });
      return result.data;
    } catch (err: any) {
      setError(err.message || 'Error sending message');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const confirmAction = async (confirmPayload: any): Promise<any> => {
    setLoading(true);
    setError(null);
    try {
      const assistantRouter = httpsCallable<any, any>(chatFunctions, 'assistantRouter');
      const result = await assistantRouter(confirmPayload);
      return result.data;
    } catch (err: any) {
      setError(err.message || 'Error confirming action');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const listConversations = async (): Promise<any> => {
    try {
      const func = httpsCallable<any, any>(chatFunctions, 'listConversations');
      const result = await func({});
      return result.data;
    } catch (err: any) {
      console.warn('Error listing conversations:', err);
      return [];
    }
  };

  const getConversationMessages = async (conversationId: string): Promise<any> => {
    setError(null);
    try {
      const func = httpsCallable<any, any>(chatFunctions, 'getConversationMessages');
      const result = await func({ conversationId });
      return result.data;
    } catch (err: any) {
      setError(err.message || 'Error loading conversation messages');
      throw err;
    }
  };

  const deleteConversation = async (conversationId: string): Promise<any> => {
    setError(null);
    try {
      const func = httpsCallable<any, any>(chatFunctions, 'deleteConversation');
      const result = await func({ conversationId });
      return result.data;
    } catch (err: any) {
      setError(err.message || 'Error deleting conversation');
      throw err;
    }
  };

  return {
    sendMessage,
    confirmAction,
    listConversations,
    getConversationMessages,
    deleteConversation,
    loading,
    error,
  };
};

export default useAIChat;